/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { classNames } from 'primereact/utils';
import { Expand, ScrollText } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from 'primereact/button';

import { Toast } from 'primereact/toast';
import { useTranslation } from 'react-i18next';

import { ButtonGroup } from 'primereact/buttongroup';
import { Dialog } from 'primereact/dialog';

import {
  CWLViewer,
  elementToFullscreen,
  extractCWLGraphFromRaw,
  Job,
  linksDefinition,
  OverLoading,
  ProcessDescription,
  ProcessPackage,
  useProcessesContext
} from 'ogc-processes-components';
import { TabPanel, TabView } from 'primereact/tabview';
import { Message } from 'primereact/message';
import { LogsViewer } from '@/pages/JobsPage/JobDetailsPage/JobDetail/LogsViewer';

import { GitFork } from 'lucide-react';
import { JobGeneralMetaCard } from '@/pages/JobsPage/JobDetailsPage/JobDetail/JobGeneralMetaCard';
import { jobCommander, useQueryFilters } from '@/hooks/useQueryFilters.tsx';
import { useNavigate } from 'react-router';
import { AppRoutes } from '@/constants/routes';
import { Utils } from '@/utils/utils';
import { TimelineExecution } from './JobDetail/TimelineExecution';
import { AntFlowJob, Project } from '@/types/models';
import { useStoreContext } from '@/contexts/StoreContext';
import { AntflowApiEndpoint } from '@/types/ogc-models';

import { GitReferenceSelector } from '@/components/GitReferenceSelector';
import { AxiosError } from 'axios';
import { TimeLineSkeleton } from './JobDetail/TimelineExecution/TimeLineSkeleton';
import { LogsAuthenticated } from './JobDetail/LogSelector';
import { AntFlowExecuteProcessForm } from '@/components/AntFlowExecuteProcessForm';

export interface JobDetailViewerProps {
  className?: string;
  job: AntFlowJob;
  refreshState: () => Promise<unknown>;
}

const findLogByName = (logsArray: string[], id: string): string | undefined => {
  const defaultLog = logsArray.find((logHref) => {
    const { pathname } = new URL(logHref);
    const fileName = pathname.split('/').pop();
    if (fileName) {
      return fileName.includes(id || '');
    }
  });
  return defaultLog;
};

export const JobDetail: React.FC<JobDetailViewerProps> = ({ className, job, refreshState }) => {
  const DEFAULT_LOG_NAME = 'stderr.log';

  const isDynaProcessJob = job.processID.startsWith('dyna~');

  const { processesEndpoint } = useProcessesContext();

  const { t } = useTranslation();

  const { currentUser } = useStoreContext();

  const navigate = useNavigate();

  const [commitRef, setCommitRef] = useState<string>(job.commit_ref);

  const { prefilters } = useQueryFilters({ commander: jobCommander, filter_prefix: 'job' });

  const fullScreenRef = useRef(null);

  const toast = useRef<Toast>(null);

  const [modalProcess, setModalProcess] = useState(false);

  const [error, setError] = useState<{ url: string; message: string }>();

  const [selectedLogId, setSelectedLogId] = useState<string | undefined>(undefined);

  const [jobProcess, setJobProcess] = useState<ProcessDescription>();

  const submitJob = useMutation({
    mutationKey: ['create_job', isDynaProcessJob],
    mutationFn: async (data: Record<string, unknown>) => {
      const endpoint = processesEndpoint as AntflowApiEndpoint;
      if (isDynaProcessJob) {
        return endpoint.post_dyna_process({
          cwl: job.cwl,
          env: job.env,
          execution_content: data
        });
      }
      const { project_path, project_ref, processID } = job;
      if (!(project_path && project_ref && processID)) {
        throw new Error(' project_path, project_ref, processID is not defined in job');
      }

      const ref = commitRef ?? project_ref;
      return endpoint.post_project_process(project_path, ref, processID, data);
    }
  });

  const cancelJob = useMutation({
    mutationKey: ['cancel_job', job],
    mutationFn: async () => {
      const _jobDefinitionLink = job.links.find((link) => link.rel === linksDefinition.self);
      if (!_jobDefinitionLink) {
        throw Error(`_jobDefinitionLink is not set in link of job ${job}`);
      }
      const endpoint = processesEndpoint as AntflowApiEndpoint;
      return endpoint.cancel_project_job(_jobDefinitionLink.href).then((res) => {
        refreshState();
        return res;
      });
    }
  });

  const getProcess = useMutation({
    mutationKey: ['get_process_from_job', job, processesEndpoint, isDynaProcessJob],
    mutationFn: async ({ projectRef }: { projectRef: string }) => {
      if (isDynaProcessJob) {
        return null;
      }
      const { project_path, project_ref, processID } = job;
      if (!(project_path && processID)) {
        throw new Error(' project_path and processID are not defined in job');
      }
      return (processesEndpoint as AntflowApiEndpoint)
        .get_project_process(project_path, projectRef ?? project_ref, processID)
        .then((processDesc) => {
          setError(undefined);
          setJobProcess(
            Utils.overrideDescription(
              processDesc,
              t(['processes.inputs.secret-helper'], { link: currentUser?.profile || '#' })
            )
          );
          return processDesc;
        })
        .catch((error: AxiosError) => {
          setError({ url: error.config?.url ?? '', message: error.message });
          throw error;
        });
    },
    onError: (_error, variables) => {
      console.error('Error fetching process description for job:', variables, _error);
      if (variables.projectRef !== job.commit_ref) {
        setCommitRef(job.commit_ref);
        toast.current?.show({
          severity: 'error',
          closable: true,
          summary: t(['notifs.ref-not-found.title'], 'Reference not found'),
          detail: t(['notifs.ref-not-found.description'], ''),
          contentClassName: 'text-left',
          className: 'text-left',
          life: 6000
        });

        getProcess.mutate({ projectRef: job.commit_ref });
      }
    }
  });

  const workflowPackage = useQuery({
    queryKey: ['get_zero_process_package', jobProcess, job.jobID],
    queryFn: async () => {
      if (!jobProcess) {
        return null;
      }
      const link = jobProcess.links?.find((el) => el.rel === linksDefinition.processPackage);
      let url = link?.href;
      if (url) {
        if (jobProcess.id.startsWith('dyna~')) {
          url = url.concat(`&cwl_format=json&publish=true`);
        } else {
          url = url.concat(`?cwl_format=json&publish=true&job_id=${job.jobID}`);
        }
        const processPackage = await processesEndpoint.get<ProcessPackage>(url);
        return extractCWLGraphFromRaw({ raw: processPackage.content, mainWorkflowId: processPackage.main, pack: true });
      } else {
        return null;
      }
    },
    throwOnError: true,
    refetchOnWindowFocus: false
  });

  const { data: project } = useQuery({
    queryKey: ['get_project_ref', jobProcess, isDynaProcessJob],
    queryFn: async () => {
      if (jobProcess && !isDynaProcessJob) {
        const link = jobProcess.links?.find((el) => el.rel === linksDefinition.project);
        if (link) {
          return processesEndpoint.get<Project>(link.href);
        }
      }
      return null;
    }
  });

  useEffect(() => {
    getProcess.mutateAsync({ projectRef: job.commit_ref }).then((process) => {
      setJobProcess(process ?? undefined);
    });
  }, [job.commit_ref, getProcess.mutateAsync]);

  const navigateToJob = useCallback(
    (job: Job) => {
      navigate(
        AppRoutes.jobs.detail
          .url(job.jobID)
          .concat(Utils.buildWithQueryParamsUrl({ job_reference: (prefilters.reference as string) || '' }))
      );
    },
    [prefilters, navigate]
  );

  const fallbackLogHref = useMemo(() => {
    if (['failed', 'successful', 'dismissed'].includes(job.status)) {
      const defaultLogHref = findLogByName(job.log_urls ?? [], DEFAULT_LOG_NAME);
      if (defaultLogHref) {
        return defaultLogHref;
      }
      return job.log_urls?.[0];
    }
  }, [job]);

  useEffect(() => {
    if (['failed', 'successful', 'dismissed'].includes(job.status)) {
      if (fallbackLogHref) {
        const filename = fallbackLogHref.split('/').pop();
        setSelectedLogId(filename);
      }
    }
  }, [job, fallbackLogHref]);

  const timeLineStatus = useMemo(() => {
    if (['running', 'created', 'accepted'].includes(job.status) && !job.progression_state) {
      return 'showLoading';
    }

    if (['failed', 'successful', 'dismissed'].includes(job.status) && !job.progression_state) {
      return 'hideTimeline';
    }

    return 'showTimeLine';
  }, [job]);

  return (
    <section className={classNames(className, 'flex flex-column align-items-start w-full')}>
      <Toast ref={toast} />

      <div className="flex flex-column  w-full">
        <JobGeneralMetaCard
          owner={
            currentUser
              ? {
                  id: currentUser.sub,
                  email: currentUser.email
                }
              : undefined
          }
          process={jobProcess}
          processIsLoading={getProcess.isPending}
          className="w-full"
          job={job}
          onRetryJob={() => {
            setModalProcess(true);
          }}
          handleCancelJob={cancelJob.mutateAsync}
        />

        <div className="w-full mb-5 mt-4">
          <TabView pt={{ panelContainer: { className: 'px-0' } }} className="mb-6 pb-3">
            <TabPanel leftIcon={<ScrollText className="mr-2" />} header={t(['misc.log_viewer', 'misc.log_viewer'])}>
              {error && (
                <Message
                  severity="error"
                  className="mt-6 text-sm w-full"
                  content={
                    <div className="text-left w-full p-2">
                      <span className="block font-semibold mb-1">Get refer process : {error.message}</span>

                      <div className="mb-2">
                        <span className="block font-bold">URL:</span>
                        <span className="block"> {error.url}</span>
                      </div>
                    </div>
                  }
                />
              )}
              {timeLineStatus === 'showTimeLine' && !workflowPackage.isPending && (
                <TimelineExecution
                  className="mt-5"
                  setSelectedLogId={setSelectedLogId}
                  workflowSteps={workflowPackage?.data?.steps}
                  job={job}
                />
              )}
              {timeLineStatus === 'showLoading' && <TimeLineSkeleton />}

              {/* Message */}
              <LogsAuthenticated
                setSelectedLogId={setSelectedLogId}
                selectedLogId={selectedLogId}
                className=" mt-3 flex flex-wrap justify-content-end  w-full  "
                logsHref={job.log_urls ?? []}
              />
              {!['running', 'created', 'accepted'].includes(job.status) && (
                <LogsViewer
                  findLogBynameFn={findLogByName}
                  fallbackLogHref={fallbackLogHref}
                  selectedLogId={selectedLogId}
                  className="mt-3"
                  job={job}
                />
              )}
            </TabPanel>

            <TabPanel
              leftIcon={<GitFork className="mr-2" />}
              className="flex flex-column justify-content-between"
              header={t(['misc.workflow_graph', 'misc.workflow_graph'])}
            >
              {jobProcess && (
                <div className="flex xl:flex-row xl:justify-content-between sm:flex-column sm:flex-column-reverse">
                  <div className="xl:col-12 sm:col-12">
                    <div className="w-full border-round " style={{ height: '30rem' }}>
                      <div className="flex justify-content-end w-full align-items-center border-gray-200  py-3 ">
                        <ButtonGroup>
                          <Button
                            tooltip={t(['actions.refresh', 'refresh'])}
                            tooltipOptions={{ className: 'text-sm', position: 'top' }}
                            id="refresh-cwl-viewer"
                            severity="contrast"
                            style={{ height: '30px' }}
                            icon="pi pi-refresh"
                          />
                          <Button
                            onClick={() => {
                              if (fullScreenRef.current) {
                                elementToFullscreen(fullScreenRef.current);
                              }
                            }}
                            tooltip={t(['actions.fullscreen', 'fullscreen'])}
                            tooltipOptions={{ className: 'text-sm', position: 'top' }}
                            severity="contrast"
                            style={{ height: '30px' }}
                            icon={<Expand size={18} />}
                          />
                        </ButtonGroup>
                      </div>
                      <CWLViewer
                        className="border-2 border-round mb-5 surface-border "
                        outerRefresh="refresh-cwl-viewer"
                        workflow={workflowPackage.data}
                        loading={workflowPackage.isLoading}
                        ref={fullScreenRef}
                      />
                    </div>
                  </div>
                </div>
              )}
            </TabPanel>
          </TabView>
        </div>
      </div>

      {modalProcess && jobProcess && (
        <Dialog
          header={jobProcess.title}
          visible={modalProcess}
          style={{ width: '60%', minHeight: '15rem' }}
          onHide={() => {
            setModalProcess(false);
          }}
        >
          <div style={{ minHeight: '25rem', height: '100%' }} className="w-full fadeinright animation-duration-100">
            {!isDynaProcessJob && (
              <div className="mt-3 mb-4">
                <label className="font-semibold block text-md mb-1" htmlFor="commit_ref">
                  {t(['misc.project_version', 'Project version'])}
                </label>
                {project && (
                  <GitReferenceSelector
                    project={project}
                    additonalReferences={[{ label: job.commit_ref, value: job.commit_ref }]}
                    reference={commitRef}
                    handleOnSelect={(e) =>
                      getProcess.mutateAsync({ projectRef: e.value }).then(() => setCommitRef(e.value))
                    }
                  />
                )}
              </div>
            )}
            {jobProcess && (
              <OverLoading loading={getProcess.isPending || submitJob.isPending}>
                <AntFlowExecuteProcessForm
                  className="pb-4"
                  execution_content={Utils.filterExecutionContent({ job, process: jobProcess })}
                  description={jobProcess.description}
                  inputs={jobProcess.inputs}
                  links={job.links}
                  onImportJobExecutionContent={(job) => {
                    const _job: Job = job;
                    _job.execution_content = Utils.filterExecutionContent({ job: _job, process: jobProcess });
                    return _job;
                  }}
                  onSubmit={({ formData }) => {
                    return submitJob.mutateAsync(formData).then(async (resp) => {
                      if (resp) {
                        toast.current?.show({
                          severity: 'success',
                          summary: t(['notifs.success.title'], 'Success'),
                          detail: t(['notifs.success.description'], 'Operation success'),
                          contentClassName: 'text-left',
                          className: 'text-left',
                          life: 6000
                        });
                        navigateToJob(resp);
                        setModalProcess(false);
                        return true;
                      } else {
                        toast.current?.show({
                          severity: 'error',
                          summary: t(['notifs.error.title'], 'Error'),
                          detail: t(['notifs.error.description'], 'Operation declined'),
                          contentClassName: 'text-left',
                          className: 'text-left',
                          life: 6000
                        });
                        return false;
                      }
                    });
                  }}
                />
              </OverLoading>
            )}
          </div>
        </Dialog>
      )}
    </section>
  );
};
